// ============================================================
// AUTH MODULE
// src/Auth.js
// Login, session management, user management page
// ============================================================

import { useState, useEffect, useCallback, createContext, useContext } from "react";
import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL  || "https://ryokwpjntufianxwdyqz.supabase.co";
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJ5b2t3cGpudHVmaWFueHdkeXF6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY0MDk1MTcsImV4cCI6MjA5MTk4NTUxN30.V9Bs4-Uijww6cB1jS1HBqL1RIHfRgs4WbiYP_mVmkFA";
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON);

const SESSION_KEY = "wom_session";

// Indian FY runs April–March — see the matching helper in App.js.
function getCurrentFYLabel() {
  const now = new Date();
  const y = now.getFullYear();
  const startYear = now.getMonth() >= 3 ? y : y - 1;
  return `FY ${startYear}–${String(startYear+1).slice(-2)}`;
}

// ── HASH PASSWORD ─────────────────────────────────────────────
async function hashPassword(password) {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2,"0")).join("");
}

// ── AUTH CONTEXT ──────────────────────────────────────────────
const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

// ── ROLE PERMISSIONS ──────────────────────────────────────────
export const ROLE_PAGES = {
  admin:     ["dashboard","work-orders","direct-work","partywise","estimates","sales","invoices","payments","ledger","recon","purchases","unmatched","customers","products","historical","temp-ledger","job-sales","users"],
  superuser: ["dashboard","work-orders","direct-work","partywise","estimates","sales","invoices","payments","ledger","recon","purchases","unmatched","customers","products","historical","temp-ledger","job-sales"],
  user:      ["dashboard","work-orders","direct-work","partywise","estimates","sales","customers"],
  staff:     ["dashboard","work-orders","direct-work","partywise","estimates","sales","customers"],
};

export function canAccess(role, page) {
  const allowed = ROLE_PAGES[role] || ROLE_PAGES["user"];
  return allowed.includes(page);
}

// ── SHARED STYLES ─────────────────────────────────────────────
const S = {
  input:      {padding:"10px 14px",border:"1px solid var(--paper3)",borderRadius:8,fontFamily:"var(--mono)",fontSize:14,color:"var(--ink)",background:"var(--paper)",outline:"none",width:"100%",boxSizing:"border-box"},
  label:      {fontSize:12,letterSpacing:"0.6px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500,display:"block",marginBottom:6},
  btnPrimary: {display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6,padding:"10px 20px",borderRadius:10,fontFamily:"var(--mono)",fontSize:14,cursor:"pointer",border:"none",background:"var(--accent)",color:"#fff",fontWeight:500,width:"100%"},
  btnGhost:   {display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:10,fontFamily:"var(--mono)",fontSize:13,cursor:"pointer",border:"1px solid var(--paper3)",background:"transparent",color:"var(--ink2)"},
  btnSm:      {padding:"5px 10px",fontSize:12,borderRadius:8,cursor:"pointer",border:"1px solid var(--paper3)",background:"transparent",color:"var(--ink2)",fontFamily:"var(--mono)"},
  btnDanger:  {padding:"5px 10px",fontSize:12,borderRadius:8,cursor:"pointer",border:"1px solid var(--danger-l)",background:"transparent",color:"var(--danger)",fontFamily:"var(--mono)"},
  card:       {background:"#fff",borderRadius:10,boxShadow:"0 2px 8px rgba(0,0,0,0.08)",overflow:"hidden"},
  th:         (r)=>({padding:"10px 16px",textAlign:r?"right":"left",fontSize:11,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500,whiteSpace:"nowrap"}),
  td:         {padding:"12px 16px",fontSize:13,color:"var(--ink)"},
  overlay:    {position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20},
};

// ── LOGIN PAGE ────────────────────────────────────────────────
function LoginPage({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [showPw, setShowPw]     = useState(false);

  const login = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) return setError("Please enter username and password.");
    setLoading(true); setError("");
    try {
      const hash = await hashPassword(password);
      const {data, error:dbErr} = await supabase.from("app_users")
        .select("id,username,full_name,role,is_active")
        .eq("username", username.trim().toLowerCase())
        .eq("password_hash", hash)
        .maybeSingle();
      if (dbErr) throw dbErr;
      if (!data) { setError("Invalid username or password."); setLoading(false); return; }
      if (!data.is_active) { setError("Your account has been deactivated. Contact the administrator."); setLoading(false); return; }
      await supabase.from("app_users").update({last_login:new Date().toISOString()}).eq("id",data.id);
      const session = { id:data.id, username:data.username, fullName:data.full_name, role:data.role, loginTime:Date.now() };
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      onLogin(session);
    } catch(e) {
      setError("Login failed: "+e.message);
    } finally {
      setLoading(false);
    }
  };

  const inp = {
    padding:"10px 14px",
    border:"1px solid #c5d3de",
    borderRadius:8,
    fontFamily:"var(--mono)",
    fontSize:14,
    color:"#1a2a3a",
    background:"#f0f4f8",
    outline:"none",
    width:"100%",
    boxSizing:"border-box",
  };

  return (
    <div style={{minHeight:"100vh",background:"#f0f4f8",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{width:"100%",maxWidth:400}}>

        {/* Brand */}
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{width:56,height:56,background:"#e6f1fb",borderRadius:14,margin:"0 auto 16px",display:"flex",alignItems:"center",justifyContent:"center",border:"1px solid #b5d4f4"}}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#185fa5" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" rx="1"/>
              <rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/>
              <rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
          </div>
          <div style={{fontSize:23,fontWeight:500,color:"#0c447c",letterSpacing:"-0.5px"}}>Super Foto DG X Press</div>
          <div style={{fontSize:12,color:"#7a9cc0",marginTop:6,letterSpacing:"0.8px",textTransform:"uppercase"}}>Work Order Management</div>
        </div>

        {/* Card */}
        <div style={{background:"#fff",border:"1px solid #dde3ea",borderRadius:16,padding:"32px 36px",boxShadow:"0 4px 20px rgba(24,95,165,0.10)"}}>
          <div style={{fontSize:19,fontWeight:500,color:"#0c447c",marginBottom:24}}>Sign in</div>

          <form onSubmit={login}>
            <div style={{marginBottom:16}}>
              <label style={{fontSize:12,letterSpacing:"0.6px",textTransform:"uppercase",color:"#7a9cc0",display:"block",marginBottom:6}}>Username</label>
              <input
                type="text"
                value={username}
                onChange={e=>setUsername(e.target.value)}
                placeholder="Enter your username"
                autoFocus
                autoComplete="username"
                style={inp}/>
            </div>

            <div style={{marginBottom:24}}>
              <label style={{fontSize:12,letterSpacing:"0.6px",textTransform:"uppercase",color:"#7a9cc0",display:"block",marginBottom:6}}>Password</label>
              <div style={{position:"relative"}}>
                <input
                  type={showPw?"text":"password"}
                  value={password}
                  onChange={e=>setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  style={{...inp,paddingRight:44}}/>
                <button type="button" onClick={()=>setShowPw(p=>!p)}
                  style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:"#7a9cc0",fontSize:17,padding:0}}>
                  {showPw?"🙈":"👁"}
                </button>
              </div>
            </div>

            {error&&(
              <div style={{marginBottom:16,padding:"10px 14px",background:"#fff0f0",border:"1px solid #f09595",borderRadius:8,fontSize:13,color:"#a32d2d"}}>
                {error}
              </div>
            )}

            <button type="submit"
              style={{width:"100%",padding:"11px",background:loading?"#0c447c":"#185fa5",border:"none",borderRadius:8,color:"#fff",fontSize:14,fontWeight:500,cursor:loading?"not-allowed":"pointer",fontFamily:"var(--mono)",transition:"background 0.15s"}}
              disabled={loading}>
              {loading ? "Signing in…" : "Sign in →"}
            </button>
          </form>
        </div>

        <div style={{textAlign:"center",marginTop:20,fontSize:12,color:"#7a9cc0"}}>
          {getCurrentFYLabel()}
        </div>
      </div>
    </div>
  );
}

// ── CHANGE PASSWORD MODAL ─────────────────────────────────────
function ChangePasswordModal({ user, onClose, toast }) {
  const [current, setCurrent]   = useState("");
  const [newPw, setNewPw]       = useState("");
  const [confirm, setConfirm]   = useState("");
  const [error, setError]       = useState("");
  const [saving, setSaving]     = useState(false);

  const save = async () => {
    setError("");
    if (!current || !newPw || !confirm) return setError("Please fill all fields.");
    if (newPw !== confirm) return setError("New passwords do not match.");
    if (newPw.length < 6) return setError("Password must be at least 6 characters.");
    setSaving(true);
    try {
      const currentHash = await hashPassword(current);
      const {data} = await supabase.from("app_users")
        .select("id").eq("id",user.id).eq("password_hash",currentHash).maybeSingle();
      if (!data) { setError("Current password is incorrect."); setSaving(false); return; }
      const newHash = await hashPassword(newPw);
      await supabase.from("app_users").update({password_hash:newHash,updated_at:new Date().toISOString()}).eq("id",user.id);
      toast("Password changed successfully!","success");
      onClose();
    } catch(e) { setError("Error: "+e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:420}}>
        <div style={{padding:"24px 28px 0",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{fontFamily:"var(--serif)",fontSize:21,fontWeight:300}}>Change password</div>
          <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
        </div>
        <div style={{padding:"20px 28px 28px"}}>
          {[
            {label:"Current password", val:current, set:setCurrent},
            {label:"New password",     val:newPw,   set:setNewPw},
            {label:"Confirm new password", val:confirm, set:setConfirm},
          ].map(f=>(
            <div key={f.label} style={{marginBottom:14}}>
              <label style={S.label}>{f.label}</label>
              <input type="password" value={f.val} onChange={e=>f.set(e.target.value)} style={S.input}/>
            </div>
          ))}
          {error&&<div style={{padding:"8px 12px",background:"var(--danger-l)",borderRadius:8,fontSize:13,color:"var(--danger)",marginBottom:14}}>{error}</div>}
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:20,paddingTop:16,borderTop:"1px solid var(--paper2)"}}>
            <button style={S.btnGhost} onClick={onClose}>Cancel</button>
            <button style={{...S.btnPrimary,width:"auto"}} onClick={save} disabled={saving}>{saving?"Saving…":"Save password"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── USER FORM MODAL ───────────────────────────────────────────
function UserFormModal({ record, onClose, onSave, toast }) {
  const isEdit = !!record;
  const [username, setUsername]   = useState(record?.username||"");
  const [fullName, setFullName]   = useState(record?.full_name||"");
  const [role, setRole]           = useState(record?.role||"user");
  const [password, setPassword]   = useState("");
  const [isActive, setIsActive]   = useState(record?.is_active??true);
  const [error, setError]         = useState("");
  const [saving, setSaving]       = useState(false);

  const save = async () => {
    setError("");
    if (!username.trim()) return setError("Username is required.");
    if (!isEdit && !password.trim()) return setError("Password is required for new users.");
    if (password && password.length < 6) return setError("Password must be at least 6 characters.");
    setSaving(true);
    try {
      if (isEdit) {
        const update = { full_name:fullName||null, role, is_active:isActive, updated_at:new Date().toISOString() };
        if (password) update.password_hash = await hashPassword(password);
        const {error:e} = await supabase.from("app_users").update(update).eq("id",record.id);
        if (e) throw e;
      } else {
        const hash = await hashPassword(password);
        const {error:e} = await supabase.from("app_users").insert({
          username:username.trim().toLowerCase(), full_name:fullName||null,
          role, password_hash:hash, is_active:true,
        });
        if (e) {
          if (e.code==="23505") throw new Error("Username already exists.");
          throw e;
        }
      }
      onSave();
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  };

const ROLES = [
  {value:"admin",     label:"Admin",      desc:"Full access to everything"},
  {value:"superuser", label:"Super User", desc:"Full access (exclusions TBD)"},
  {value:"user",      label:"User",       desc:"Work orders, Direct work, Estimates, Sales, Customers"},
  {value:"staff",     label:"Staff",      desc:"Work orders, Direct work, Estimates, Sales, Customers"},
];

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:480}}>
        <div style={{padding:"24px 28px 0",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{fontFamily:"var(--serif)",fontSize:21,fontWeight:300}}>{isEdit?"Edit user":"Add user"}</div>
          <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
        </div>
        <div style={{padding:"20px 28px 28px"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            <div>
              <label style={S.label}>Username *</label>
              <input value={username} onChange={e=>setUsername(e.target.value)} placeholder="e.g. john" style={S.input} disabled={isEdit}/>
              {isEdit&&<div style={{fontSize:11,color:"var(--ink3)",marginTop:3}}>Username cannot be changed</div>}
            </div>
            <div>
              <label style={S.label}>Full name</label>
              <input value={fullName} onChange={e=>setFullName(e.target.value)} placeholder="e.g. John Smith" style={S.input}/>
            </div>
            <div style={{gridColumn:"span 2"}}>
              <label style={S.label}>{isEdit?"Reset password (leave blank to keep current)":"Password *"}</label>
              <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder={isEdit?"Enter new password to reset…":"Min 6 characters"} style={S.input}/>
            </div>
            <div style={{gridColumn:"span 2"}}>
              <label style={S.label}>Role *</label>
              <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:4}}>
                {ROLES.map(r=>(
                  <div key={r.value} onClick={()=>setRole(r.value)}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",border:`2px solid ${role===r.value?"var(--accent)":"var(--paper3)"}`,borderRadius:10,cursor:"pointer",background:role===r.value?"var(--accent-l)":"#fff",transition:"all 0.15s"}}>
                    <input type="radio" checked={role===r.value} onChange={()=>setRole(r.value)} style={{accentColor:"var(--accent)",flexShrink:0}}/>
                    <div>
                      <div style={{fontWeight:500,fontSize:13}}>{r.label}</div>
                      <div style={{fontSize:11,color:"var(--ink3)",marginTop:1}}>{r.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {isEdit&&(
              <div style={{gridColumn:"span 2"}}>
                <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",userSelect:"none",fontSize:13}}>
                  <input type="checkbox" checked={isActive} onChange={e=>setIsActive(e.target.checked)} style={{width:14,height:14,accentColor:"var(--accent)"}}/>
                  Account active
                </label>
              </div>
            )}
          </div>
          {error&&<div style={{padding:"8px 12px",background:"var(--danger-l)",borderRadius:8,fontSize:13,color:"var(--danger)",marginTop:14}}>{error}</div>}
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:20,paddingTop:16,borderTop:"1px solid var(--paper2)"}}>
            <button style={S.btnGhost} onClick={onClose}>Cancel</button>
            <button style={{...S.btnPrimary,width:"auto"}} onClick={save} disabled={saving}>{saving?"Saving…":isEdit?"Save changes":"Add user"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── USERS PAGE ────────────────────────────────────────────────
export function UsersPage({ toast }) {
  const { user } = useAuth();
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const {data} = await supabase.from("app_users").select("*").order("created_at");
    setUsers(data||[]); setLoading(false);
  },[]);

  useEffect(()=>{load();},[load]);

  const deleteUser = async (u) => {
    if (u.id===user.id) return alert("You cannot delete your own account.");
    if (!window.confirm(`Delete user "${u.username}"? This cannot be undone.`)) return;
    await supabase.from("app_users").delete().eq("id",u.id);
    toast("User deleted.","error"); load();
  };

const ROLE_COLORS = {
  admin:     {bg:"var(--danger-l)",  color:"var(--danger)"},
  superuser: {bg:"#f3e8ff",          color:"#6a1b9a"},
  user:      {bg:"var(--accent-l)",  color:"var(--accent)"},
  staff:     {bg:"var(--info-l)",    color:"var(--info)"},
};

  return (
    <div>
      <div style={{display:"flex",alignItems:"flex-end",justifyContent:"space-between",marginBottom:28,paddingBottom:20,borderBottom:"1px solid var(--paper3)"}}>
        <div>
          <div style={{fontFamily:"var(--serif)",fontSize:29,fontWeight:300,letterSpacing:"-0.5px"}}>Users</div>
          <div style={{fontSize:12,color:"var(--ink3)",marginTop:6}}>Manage user accounts and access roles</div>
        </div>
        <button style={{...S.btnPrimary,width:"auto"}} onClick={()=>setModal("new")}>+ Add user</button>
      </div>

      <div style={S.card}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
            <th style={S.th()}>Username</th>
            <th style={S.th()}>Full name</th>
            <th style={S.th()}>Role</th>
            <th style={S.th()}>Status</th>
            <th style={S.th()}>Last login</th>
            <th style={S.th()}>Actions</th>
          </tr></thead>
          <tbody>
            {loading&&<tr><td colSpan={6} style={{padding:40,textAlign:"center",color:"var(--ink3)"}}>Loading…</td></tr>}
            {!loading&&users.map(u=>(
              <tr key={u.id} style={{borderBottom:"1px solid var(--paper2)"}}>
                <td style={S.td}>
                  <div style={{fontFamily:"var(--mono)",fontWeight:500}}>{u.username}</div>
                  {u.id===user.id&&<div style={{fontSize:11,color:"var(--accent)"}}>← you</div>}
                </td>
                <td style={S.td}>{u.full_name||"—"}</td>
                <td style={S.td}>
                  <span style={{display:"inline-block",padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:500,
                    background:ROLE_COLORS[u.role]?.bg||"var(--paper2)",
                    color:ROLE_COLORS[u.role]?.color||"var(--ink3)"}}>
                    {u.role}
                  </span>
                </td>
                <td style={S.td}>
                  <span style={{display:"inline-block",padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:500,
                    background:u.is_active?"var(--accent-l)":"var(--paper2)",
                    color:u.is_active?"var(--accent)":"var(--ink3)"}}>
                    {u.is_active?"Active":"Inactive"}
                  </span>
                </td>
                <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12,color:"var(--ink3)"}}>
                  {u.last_login?new Date(u.last_login).toLocaleString("en-IN",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"}):"Never"}
                </td>
                <td style={S.td}>
                  <div style={{display:"flex",gap:6}}>
                    <button style={S.btnSm} onClick={()=>setModal(u)}>Edit</button>
                    {u.id!==user.id&&<button style={S.btnDanger} onClick={()=>deleteUser(u)}>Delete</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal&&<UserFormModal record={modal==="new"?null:modal} onClose={()=>setModal(null)} onSave={()=>{setModal(null);load();toast(modal==="new"?"User added!":"User updated!");}} toast={toast}/>}
    </div>
  );
}

// ── AUTH PROVIDER ─────────────────────────────────────────────
export function AuthProvider({ children, toast }) {
  const [user, setUser]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [changePwModal, setChangePwModal] = useState(false);
const showToast = toast || ((msg) => alert(msg));

  useEffect(() => {
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) {
        const session = JSON.parse(stored);
        // Session expires after 12 hours
        if (Date.now() - session.loginTime < 12 * 60 * 60 * 1000) {
          setUser(session);
        } else {
          localStorage.removeItem(SESSION_KEY);
        }
      }
    } catch { localStorage.removeItem(SESSION_KEY); }
    setLoading(false);
  }, []);

  const login = (session) => setUser(session);

  const logout = () => {
    localStorage.removeItem(SESSION_KEY);
    setUser(null);
  };

  if (loading) {
    return (
      <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--paper)"}}>
        <div style={{textAlign:"center"}}>
          <div style={{fontFamily:"var(--serif)",fontSize:21,fontWeight:300,color:"var(--ink3)"}}>Loading…</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage onLogin={login}/>;
  }

  return (
    <AuthContext.Provider value={{user, logout, setChangePwModal}}>
      {children}
      {changePwModal&&(
        <ChangePasswordModal
          user={user}
          onClose={()=>setChangePwModal(false)}
toast={showToast}
        />
      )}
    </AuthContext.Provider>
  );
}
